import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { getRecap } from '../../api/recap.ts';
import { claves } from '../../api/queries.ts';
import { ApiErrorState } from '../../components/ApiErrorState.tsx';
import { RECAP_DIAS_POR_DEFECTO } from '../../config.ts';
import { compartirTexto, tieneWebShare, type ResultadoCompartir } from './compartir.ts';

interface Props {
  leagueId: string;
}

/**
 * Resumen del periodo, BAJO DEMANDA: se pide al pulsar, no al abrir la pantalla.
 *
 * Es deliberado. La clasificación se consulta muchas veces al día y el recap es un gesto
 * puntual —"voy a picar al grupo"—, así que cargarlo siempre serían tres consultas de
 * más por visita para algo que casi nadie mira en ese momento.
 */
export function RecapPanel({ leagueId }: Props) {
  const { t } = useTranslation();
  const [pedido, setPedido] = useState(false);
  const [resultado, setResultado] = useState<ResultadoCompartir | null>(null);

  // Se resuelve una sola vez al montar y no en cada render: así el recorrido E2E puede
  // preparar el navegador con o sin Web Share antes de cargar la página y comprobar de
  // verdad los dos botones.
  const [hayWebShare] = useState(() => tieneWebShare(navigator));

  const { data, isFetching, error } = useQuery({
    queryKey: claves.ligaRecap(leagueId, RECAP_DIAS_POR_DEFECTO),
    queryFn: () => getRecap(leagueId, RECAP_DIAS_POR_DEFECTO),
    enabled: pedido,
  });

  async function compartir() {
    if (!data) return;
    setResultado(await compartirTexto(data.text, navigator, data.league.name));
  }

  return (
    <section className="mt-6" data-test="recap">
      {!pedido ? (
        <button
          type="button"
          className="btn-secundario w-full"
          onClick={() => setPedido(true)}
          data-test="generar-recap"
        >
          {t('recap.generate')}
        </button>
      ) : null}

      {pedido && isFetching ? (
        <p className="text-center text-sm text-tenue" data-test="recap-cargando">
          {t('recap.pending')}
        </p>
      ) : null}

      <ApiErrorState error={error} />

      {data ? (
        <div className="tarjeta" data-test="recap-vista-previa">
          <h2 className="mb-2 text-sm font-bold">{t('recap.title')}</h2>

          {/*
            `whitespace-pre-wrap` conserva los saltos de línea del backend, que son los
            que hacen que el mensaje se lea en WhatsApp. Sin él, un <pre> normal no los
            ajusta al ancho y la tarjeta saca barra horizontal en cuanto una línea pasa
            de los 360 px; el recorrido E2E lo mide con el móvil emulado.

            `break-words` es un cinturón, no un tirante: el nombre de usuario está topado
            en 24 caracteres y quitarlo NO desborda nada a 360 px —comprobado rompiéndolo
            a propósito—. Se deja porque cuesta cero y porque el día que se amplíe ese
            tope, o que un nombre de equipo entre en el texto, la palabra suelta ya no
            cabrá. No se le atribuye un mérito que hoy no tiene.
          */}
          <pre
            className="mb-3 max-h-80 overflow-y-auto whitespace-pre-wrap break-words font-sans text-sm leading-relaxed"
            data-test="recap-texto"
          >
            {data.text}
          </pre>

          <button
            type="button"
            className="btn-primario"
            onClick={() => void compartir()}
            data-test="compartir-recap"
          >
            {hayWebShare ? t('recap.share') : t('recap.copy')}
          </button>

          {resultado === 'copiado' ? (
            <p className="aviso-info mt-3" role="status" data-test="recap-copiado">
              {t('recap.copied')}
            </p>
          ) : null}
          {resultado === 'sin-soporte' ? (
            <p className="aviso-atencion mt-3" role="status" data-test="recap-sin-soporte">
              {t('recap.copyManually')}
            </p>
          ) : null}
        </div>
      ) : null}
    </section>
  );
}
